// @ts-strict-ignore
import { IArticleConnectProcessor } from './i-article-connect.processor';
import { ProjectModel } from '@project/model/project.model';
import { IOperation } from '@shared/undo/operation/i-operation';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { ArticleContainersWrapper } from '@3d-scene/model3d/article-containers-wrapper';
import { Connection } from '@3d-scene/model3d/connection';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { CompositeConnectionUtils } from '../../../util/composite-connection-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { Line } from '@3d-scene/model/line';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Matrix4, Vector3 } from 'three';
import { IntRangeVO } from '../../../model/vo/property.vo';
import { Dictionary } from 'typescript-collections';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ConnectArticleHelper } from '@article/helper/connect-article.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { CollisionHelper } from '@3d-scene/helper/collision.helper';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { AttachedArticleContainer } from '../../../service/article-container-attached.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

export class BaseMultipleConnectionConnectProcessor implements IArticleConnectProcessor {
  protected _createdUndoOperations: IOperation[] = null;

  private _usageIdsToDetach: string[] = null;

  constructor(
    protected projectModel: ProjectModel,
    protected projectModelService: ProjectModelService,
    protected projectArticleService: ProjectArticleService,
    protected projectMetaElementService: ProjectMetaElementService,
    protected localeResourceManager: LocaleResourceManager,
    private articleContainerLoader: ArticleContainerLoader,
    private compositeArticleCacheManager: CompositeArticleCacheManager,
    private articleContainerFactory: ArticleContainerFactory,
    private articleManager: ArticleManager,
    private compositeArticleUtils: CompositeArticleUtils,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    protected connectionInfoService: ConnectionInfoService,
    protected containerService: ContainerService,
    protected articleContainerUtils: ArticleContainerUtils,
    private detachArticleUsageService: DetachArticleUsageService,
  ) {}

  protected collisionCheckRequired(article: ArticleVO): boolean {
    return false;
  }

  protected get reversedConnectionAvailable(): boolean {
    return true;
  }

  protected get articleTypesIncludedInCollision(): string[] {
    return [ArticleTypeConstants.TYPE_BRIDGE];
  }

  public get createdUndoOperations(): IOperation[] {
    return this._createdUndoOperations;
  }

  public clear(): void {
    this._createdUndoOperations = null;
  }

  public preProcess(
    article: ArticleVO,
    connectArticlePrepareDatas: ConnectArticlePrepareData[],
    handler: Function,
    usageIdsToDetach: string[] = null
  ): void {
    this._usageIdsToDetach = usageIdsToDetach;
    this.internalPreProcess(article, connectArticlePrepareDatas, handler);
  }

  protected internalPreProcess(article: ArticleVO, connectArticlePrepareDatas: ConnectArticlePrepareData[], handler: Function): void {
    this.finalizePreProcess(article, connectArticlePrepareDatas, null, handler);
  }

  protected finalizePreProcess(
    article: ArticleVO,
    connectArticlePrepareDatas: ConnectArticlePrepareData[],
    foundUsageIdsToDetach: string[],
    handler: Function
  ): void {
    let articleWrappers: ArticleContainersWrapper[] = [];
    for (const connectPrepareData of connectArticlePrepareDatas) {
      articleWrappers = articleWrappers.concat(this.createArticleWrappersByPrepareData(article, connectPrepareData));
    }
    handler(articleWrappers, foundUsageIdsToDetach);
  }

  protected getGroupId(article: ArticleVO): string {
    return null;
  }

  protected collectAcceptingCubesByPrepareDatas(connectArticlePrepareDatas: ConnectArticlePrepareData[]): Connection[] {
    if (!connectArticlePrepareDatas) {
      return [];
    }
    const acceptingCubes: Connection[] = [];
    for (const prepareData of connectArticlePrepareDatas) {
      ArrayUtils.mergeArrays(acceptingCubes, prepareData.acceptingConnections);
    }
    return acceptingCubes;
  }

  private createArticleWrappersByPrepareData(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData): ArticleContainersWrapper[] {
    const createdWrappers: ArticleContainersWrapper[] = [];
    if (connectPrepareData.givingConnectionNames.length > 1) {
      const multipleConnectionsArticleWrapper: ArticleContainersWrapper = this.createArticleWrapper(
        article,
        connectPrepareData,
        connectPrepareData.acceptingConnections
      );
      createdWrappers.push(multipleConnectionsArticleWrapper);
    } else {
      const groupId: string = this.getGroupId(article);
      for (const cube of connectPrepareData.acceptingConnections) {
        const singleConnectionArticleWrapper: ArticleContainersWrapper = this.createArticleWrapper(article, connectPrepareData, [cube], groupId);
        createdWrappers.push(singleConnectionArticleWrapper);
      }
    }
    return createdWrappers;
  }

  private createArticleWrapper(article: ArticleVO, connectPrepareData: ConnectArticlePrepareData, acceptingConnectionCubes: Connection[],
    groupId: string = null): ArticleContainersWrapper {
    const articlesWrapper: ArticleContainersWrapper = new ArticleContainersWrapper(
      article,
      groupId,
      ArticleTypeConstants.EXCLUDED_TYPES_FROM_COMPOSITE_ARTICLE,
      this.projectModel,
      this.projectArticleService,
      this.articleContainerLoader,
      this.compositeArticleCacheManager,
      this.articleContainerFactory,
      this.articleManager,
      this.articleContainerUtils,
      this.detachArticleUsageService
    );
    articlesWrapper.searchVectorId = connectPrepareData.vectorId;
    articlesWrapper.givingConnectionNames = connectPrepareData.givingConnectionNames;
    articlesWrapper.properties.setValue(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS, acceptingConnectionCubes);
    return articlesWrapper;
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number
  ): void {
    let acceptingConnectionCubes: Connection[] = baseContainer.properties.getValue(ArticleContainerPropertyDef.ACCEPTING_CONNECTIONS);
    let givingConnectionCubes: Connection[] = this.getGivingConnectionCubes(containers, baseContainer, givingConnectionAreas);

    if (acceptingConnectionCubes && acceptingConnectionCubes.length > 0 && acceptingConnectionCubes.length === givingConnectionCubes.length) {
      acceptingConnectionCubes = this.sortConnectionsByDirection(acceptingConnectionCubes, CommonConstants.DIRECTION_U);
      givingConnectionCubes = this.sortConnectionsByDirection(givingConnectionCubes, CommonConstants.DIRECTION_V);
      let articleConnected: boolean = this.connectAndAttachArticles(containers, baseContainer, givingConnectionCubes, acceptingConnectionCubes);
      if (!articleConnected && this.reversedConnectionAvailable) {
        givingConnectionCubes.reverse();
        articleConnected = this.connectAndAttachArticles(containers, baseContainer, givingConnectionCubes, acceptingConnectionCubes);
      }

      if (!articleConnected) {
        throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.connections_not_suitable'));
      }
    } else {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.no_connection'));
    }
  }

  private getGivingConnectionCubes(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[]
  ): Connection[] {
    let givingCubes: Connection[] = [];
    if (this.compositeArticleUtils.isCompositeMultipleBaseArticle(this.compositeArticleInfoUtils.getCompositeArticleFromContainer(baseContainer))) {
      givingCubes = CompositeConnectionUtils.findGivingCubesByCompositeAreas(containers, givingConnectionAreas);
    } else {
      givingCubes = ConnectionInfoUtils.getConnectionCubesByMetaElements(baseContainer, givingConnectionAreas);
    }
    return givingCubes;
  }

  public postProcess(containers: ArticleContainer[]): void {}

  private sortConnectionsByDirection(sortedConnectionCubes: Connection[], direction: string): Connection[] {
    if (!sortedConnectionCubes || sortedConnectionCubes.length === 0) {
      return [];
    }
    if (sortedConnectionCubes.length > 1) {
      const firstCube: Connection = sortedConnectionCubes[0];
      const lastCube: Connection = sortedConnectionCubes[sortedConnectionCubes.length - 1];
      const rect: Rectangle3D = firstCube.compositionRectangle;
      let line: Line = null;

      switch (direction) {
        case CommonConstants.DIRECTION_U: {
          line = new Line(rect.point1, rect.point4);
          break;
        }
        case CommonConstants.DIRECTION_V: {
          line = new Line(rect.point1, rect.point2);
          break;
        }
      }

      if (line && MathUtils.calculateConnectionRecsDistanceOnLine(firstCube.compositionRectangle, lastCube.compositionRectangle, line) < 0) {
        sortedConnectionCubes.reverse();
      }
    }
    return sortedConnectionCubes;
  }

  private compareConnectionProperties(sortedAcceptingCubes: Connection[], sortedGivingCubes: Connection[]): boolean {
    if (
      !sortedAcceptingCubes ||
      !sortedGivingCubes ||
      sortedAcceptingCubes.length === 0 ||
      sortedAcceptingCubes.length !== sortedGivingCubes.length
    ) {
      return false;
    }

    for (let i: number = 0; i < sortedAcceptingCubes.length; i++) {
      if (!this.connectionInfoService.checkConnectionProperties(sortedGivingCubes[i], sortedAcceptingCubes[i])) {
        return false;
      }
    }

    return true;
  }

  private connectAndAttachArticles(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionCubes: Connection[],
    acceptingConnectionCubes: Connection[]
  ): boolean {
    const articleConnected: boolean = this.connectArticleContainers(containers, baseContainer, givingConnectionCubes, acceptingConnectionCubes);
    if (articleConnected) {
      this.createMetaElementUsages(containers, givingConnectionCubes, acceptingConnectionCubes);
      this.finalizeAttachArticleContainer(containers, acceptingConnectionCubes);
    }
    return articleConnected;
  }

  private connectArticleContainers(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionCubes: Connection[],
    acceptingConnectionCubes: Connection[]
  ): boolean {
    let deltaOffset: Vector3 = null;
    if (givingConnectionCubes.length > 1) {
      // we don't really need to check matching by requirements and features for single accepting/giving connection here,
      // so it's checked only in case when article connected by multiple giving connections
      if (!this.compareConnectionProperties(acceptingConnectionCubes, givingConnectionCubes)) {
        return false;
      }
      const uDistances: IntRangeVO[] = this.calculatePinsDistances(acceptingConnectionCubes, givingConnectionCubes, CommonConstants.DIRECTION_U);
      const vDistances: IntRangeVO[] = this.calculatePinsDistances(acceptingConnectionCubes, givingConnectionCubes, CommonConstants.DIRECTION_V);
      const nDistances: IntRangeVO[] = this.calculatePinsDistances(acceptingConnectionCubes, givingConnectionCubes, CommonConstants.DIRECTION_N);
      if (uDistances.length === 0 || vDistances.length === 0 || nDistances.length === 0) {
        return false; // there are no available delta-ranges to connect articles
      }
      const uOffset: number = (uDistances[0].left + uDistances[0].right) / 2;
      const vOffset: number = (vDistances[0].left + vDistances[0].right) / 2;
      const nOffset: number = nDistances[0].left;
      deltaOffset = new Vector3(uOffset, vOffset, nOffset);
    }

    let usageIdToArticleTransformation: Dictionary<string, Matrix4> = null;
    if (this.collisionCheckRequired(baseContainer.article)) {
      usageIdToArticleTransformation = this.storeDefaultArticlesTransformation(containers);
    }

    this.applyTransformationToContainersByCubes(containers, givingConnectionCubes, acceptingConnectionCubes, deltaOffset);

    let articlesCollided: boolean = false;
    if (usageIdToArticleTransformation) {
      articlesCollided = this.checkArticlesCollision(containers);
      if (articlesCollided) {
        this.restoreDefaultArticlesTransformation(containers, usageIdToArticleTransformation);
      }
    }
    return articlesCollided ? false : true;
  }

  /**
   * Method applies additional rotation and transformation to given containers by fitting accepting/giving connections.
   *
   * @param containers article containers which should be transformed
   * @param givingCubes array of suitable giving connections
   * @param acceptingCubes array of accepting connections
   * @param deltaOffset additional offset calculated by uvn-delta values, where x: u-delta, y: v-delta, z: n-delta
   */
  private applyTransformationToContainersByCubes(
    containers: ArticleContainer[],
    givingCubes: Connection[],
    acceptingCubes: Connection[],
    deltaOffset: Vector3
  ): void {
    const acceptingRectangle: Rectangle3D = this.articleContainerUtils.calculateBridgeRectangle(acceptingCubes, false);
    const givingRectangle: Rectangle3D = this.articleContainerUtils.calculateBridgeRectangle(givingCubes, true);
    const rotMatrix: Matrix4 = MathUtils.getRotationMatrix(givingRectangle.toFace(), acceptingRectangle.toFace());

    const firstAcceptingRectangle: Rectangle3D = acceptingCubes[0].compositionRectangle;
    if (deltaOffset) {
      MathUtils.moveRectangle(firstAcceptingRectangle, deltaOffset.z, CommonConstants.DIRECTION_N);
    }
    const firstGivingRectangle: Rectangle3D = givingCubes[0].compositionRectangle;

    let acceptingMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(firstAcceptingRectangle);
    if (deltaOffset) {
      acceptingMiddlePoint = this.movePointByLine(acceptingMiddlePoint, acceptingRectangle.point1, acceptingRectangle.point4, -deltaOffset.y);
    }
    let givingMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(firstGivingRectangle);
    givingMiddlePoint = MathUtils.rotate([givingMiddlePoint], rotMatrix)[0];
    givingMiddlePoint = acceptingMiddlePoint.clone().sub(givingMiddlePoint);

    ConnectArticleHelper.connectArticleContainers(containers, rotMatrix, givingMiddlePoint);
  }

  private storeDefaultArticlesTransformation(containers: ArticleContainer[]): Dictionary<string, Matrix4> {
    const usageIdToArticleTransformation: Dictionary<string, Matrix4> = new Dictionary<string, Matrix4>();
    for (const container of containers) {
      const articleTransformation: Matrix4 = container.matrix.clone();
      usageIdToArticleTransformation.setValue(container.articleGUID, articleTransformation);
    }
    return usageIdToArticleTransformation;
  }

  private restoreDefaultArticlesTransformation(containers: ArticleContainer[], usageIdToArticleTransformation: Dictionary<string, Matrix4>): void {
    if (!usageIdToArticleTransformation) {
      return;
    }
    for (const container of containers) {
      if (!usageIdToArticleTransformation.containsKey(container.articleGUID)) {
        continue;
      }
      const articleTransformation: Matrix4 = usageIdToArticleTransformation.getValue(container.articleGUID);
      container.matrix.copy(new Matrix4());
      container.applyMatrix4(articleTransformation);
    }
  }

  protected createMetaElementUsages(
    givingArticleContainers: ArticleContainer[],
    givingConnectionCubes: Connection[],
    acceptingConnectionCubes: Connection[]
  ): void {
    for (let i: number = 0; i < givingConnectionCubes.length; i++) {
      this.projectMetaElementService.createUsageBetweenConnections(givingConnectionCubes[i], acceptingConnectionCubes[i]);
    }
    this.articleContainerUtils.updateActiveSetInArticleContainers(givingArticleContainers);
  }

  protected finalizeAttachArticleContainer(givingArticleContainers: ArticleContainer[], acceptingConnectionCubes: Connection[]): void {
    // dispatch attach message for each created containers
    // articles will be attached to the composition container taken by the first accepting article
    const acceptingArticleContainer: ArticleContainer = this.containerService.getArticleContainer(acceptingConnectionCubes[0]);
    const compositionContainer: CompositionContainer = this.containerService.getCompositionContainer(acceptingArticleContainer);
    for (const container of givingArticleContainers) {
      const data: AttachedArticleContainer = {
        articleGUID: container.articleGUID,
        compositionId: compositionContainer.compositionGuid,
        container: container,
        connectedTo: ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage),
      };
      this.containerService.attachArticleContainer(data)
    }
  }

  private movePointByLine(point: Vector3, startLinePoint: Vector3, endLinePoint: Vector3, distance: number): Vector3 {
    const lineDistance: number = startLinePoint.distanceTo(endLinePoint);
    const offset: Vector3 = MathUtils.getInterpolated(startLinePoint, endLinePoint, (lineDistance - distance) / lineDistance);
    offset.sub(startLinePoint);

    return point.clone().add(offset);
  }

  private calculatePinsDistances(acceptingConnections: Connection[], givingConnections: Connection[], direction: string): IntRangeVO[] {
    let rangeValues: IntRangeVO[] = [];
    if (acceptingConnections.length === givingConnections.length) {
      if (acceptingConnections.length === 1) {
        // connect article with single giving connection
        const emptyRange: IntRangeVO = new IntRangeVO();
        emptyRange.left = 0;
        emptyRange.right = 0;

        return [emptyRange];
      }

      const acceptingRectangle: Rectangle3D = this.articleContainerUtils.calculateBridgeRectangle(acceptingConnections, false);
      const givingRectangle: Rectangle3D = this.articleContainerUtils.calculateBridgeRectangle(givingConnections, true);

      let acceptingLine: Line;
      let givingLine: Line;
      switch (direction) {
        case CommonConstants.DIRECTION_U:
          acceptingLine = new Line(acceptingRectangle.point1, acceptingRectangle.point2);
          givingLine = new Line(givingRectangle.point1, givingRectangle.point4);
          break;
        case CommonConstants.DIRECTION_V:
          acceptingLine = new Line(acceptingRectangle.point1, acceptingRectangle.point4);
          givingLine = new Line(givingRectangle.point1, givingRectangle.point2);
          break;
        case CommonConstants.DIRECTION_N:
          const acceptingNormale: Vector3 = MathUtils.getNormalFromFirstPoint(
            acceptingRectangle.point3,
            acceptingRectangle.point2,
            acceptingRectangle.point1
          );
          acceptingNormale.add(acceptingRectangle.point1);
          const givingNormale: Vector3 = MathUtils.getNormalFromFirstPoint(givingRectangle.point1, givingRectangle.point2, givingRectangle.point3);
          givingNormale.add(givingRectangle.point1);
          acceptingLine = new Line(acceptingRectangle.point1, acceptingNormale);
          givingLine = new Line(givingRectangle.point1, givingNormale);
          break;
        default:
          return [];
      }

      rangeValues = this.calculateConnectionPinRanges(acceptingConnections, acceptingLine, givingConnections, givingLine, direction);
    }

    return rangeValues;
  }

  private calculateConnectionPinRanges(
    acceptingConnections: Connection[],
    acceptingLine: Line,
    givingConnections: Connection[],
    givingLine: Line,
    direction: string
  ): IntRangeVO[] {
    // in case delta distances have been configured for complex articles we should use sum of such distances for each giving connection
    const sumOfDeltaDistances: number = this.calculateSumOfDeltaDistances(givingConnections);
    const deltaPinRanges: IntRangeVO[] = [];
    for (let i: number = 0; i < acceptingConnections.length - 1; i++) {
      const acceptingDistance: number =
        MathUtils.calculateConnectionRecsDistanceOnLine(
          acceptingConnections[i].compositionRectangle,
          acceptingConnections[i + 1].compositionRectangle,
          acceptingLine
        ) + CommonConstants.CONSTANT_OFFSET;
      const givingDistance: number =
        MathUtils.calculateConnectionRecsDistanceOnLine(
          givingConnections[i].compositionRectangle,
          givingConnections[i + 1].compositionRectangle,
          givingLine
        ) + CommonConstants.CONSTANT_OFFSET;

      let leftRange: IntRangeVO;
      let rightRange: IntRangeVO;
      const currentCube: Connection = givingConnections[i];
      if (!deltaPinRanges[i]) {
        if (sumOfDeltaDistances > 0) {
          leftRange = PropertyUtils.getDeltaRange(sumOfDeltaDistances, direction);
        } else {
          leftRange = PropertyUtils.getDeltaRangeByConnection(currentCube.metaElementVO as ConnectionAreaVO, direction);
        }
      } else {
        leftRange = deltaPinRanges[i];
      }

      if (sumOfDeltaDistances > 0) {
        rightRange = PropertyUtils.getDeltaRange(sumOfDeltaDistances, direction);
      } else {
        rightRange = PropertyUtils.getDeltaRangeByConnection(givingConnections[i + 1].metaElementVO as ConnectionAreaVO, direction);
      }
      const distanceRange: IntRangeVO = this.calculatePinDistanceRange(givingDistance, leftRange, rightRange);

      if (MathUtils.compare(acceptingDistance, distanceRange.left) > -1 && MathUtils.compare(acceptingDistance, distanceRange.right) < 1) {
        const leftDeltaSize: number = leftRange.right - leftRange.left;
        const rightDeltaSize: number = rightRange.right - rightRange.left;
        const distanceOffset: number = acceptingDistance - distanceRange.left;

        const newLeftRange: IntRangeVO = new IntRangeVO();
        const newRightRange: IntRangeVO = new IntRangeVO();

        if (distanceOffset >= leftDeltaSize) {
          newLeftRange.left = leftRange.left;
          newRightRange.left = rightRange.left + (distanceOffset - leftDeltaSize);
        } else {
          newRightRange.left = rightRange.left;
          newLeftRange.left = leftRange.right - distanceOffset;
        }

        if (distanceOffset >= rightDeltaSize) {
          newRightRange.right = rightRange.right;
          newLeftRange.right = leftRange.right - (distanceOffset - rightDeltaSize);
        } else {
          newLeftRange.right = leftRange.right;
          newRightRange.right = rightRange.left + distanceOffset;
        }

        for (const pinRange of deltaPinRanges) {
          pinRange.left += newLeftRange.left - leftRange.left;
          pinRange.right += newLeftRange.right - leftRange.right;
        }

        deltaPinRanges[i] = newLeftRange;
        deltaPinRanges[i + 1] = newRightRange;
      } else {
        return [];
      }
    }

    return deltaPinRanges;
  }

  private calculateSumOfDeltaDistances(givingConnections: Connection[]): number {
    let sumDeltaDistances: number = 0;
    if (givingConnections.length > 0 && ArticleUsageUtils.isCompositePartArticleUsage(givingConnections[0].articleContainer.articleUsage)) {
      for (const cube of givingConnections) {
        const distance: number = ConnectionInfoUtils.getDeltaDistance(cube.metaElementVO);
        if (!isNaN(distance)) {
          sumDeltaDistances += distance;
        }
      }
    }
    return sumDeltaDistances;
  }

  private calculatePinDistanceRange(distance: number, leftDeltaRange: IntRangeVO, rightDeltaRange: IntRangeVO): IntRangeVO {
    const distanceRange: IntRangeVO = new IntRangeVO();
    distanceRange.left = distance - leftDeltaRange.right + rightDeltaRange.left;
    distanceRange.right = distance - leftDeltaRange.left + rightDeltaRange.right;

    return distanceRange;
  }

  /**
   * @return true if one or more target containers collided with another article in 3d scene, otherwise false
   */
  private checkArticlesCollision(targetArticleContainers: ArticleContainer[]): boolean {
    let excludedUsageIds: string[] = ArticleContainerUtils.extractArticleGUIDs(targetArticleContainers);
    if (!ArrayUtils.isEmpty(this._usageIdsToDetach)) {
      excludedUsageIds = excludedUsageIds.concat(this._usageIdsToDetach);
    }
    const sourceArticleContainers: ArticleContainer[] = [];
    const sourceArticleUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByTypes(this.articleTypesIncludedInCollision);
    for (const usage of sourceArticleUsages) {
      if (excludedUsageIds.indexOf(usage.id) === -1) {
        const sourceContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usage.id);
        // Don't check collision with wire bridge articles
        if (sourceContainer && sourceContainer.article && !ArticleUtils.isWireBridgeArticle(sourceContainer.article)) {
          sourceArticleContainers.push(sourceContainer);
        }
      }
    }

    for (const targetContainer of targetArticleContainers) {
      if (CollisionHelper.isCollisionDetected(targetContainer, sourceArticleContainers, true, true, false)) {
        return true;
      }
    }
    return false;
  }
}
